import { applyBranding } from './branding/brandingProvider';
import { applyCountry } from './country/countryProvider';

/** Feature flags supported by Role Requirements */
export interface Features {
  /** If true, show search */
  search: boolean;

  /** If true, show help icon and associated help text */
  help: boolean;

  /** If true, show icon next to heading */
  jobStreetIcon: boolean;

  /** If true, show 'new' pill next to heading */
  newPill: boolean;

  /** If false, hide the 'Questions for candidates' header */
  showHeader: boolean;

  /** If true, show a tooltip next to the 'Must Have' toggle */
  mustHaveToolTip: boolean;

  /** If true, show a 'new' pill next to the 'Must Have' toggle and an alert at the top of the widget */
  mustHaveNew: boolean;

  /** If true, show a published job alert in header component at the top of the widget */
  showPublishedHeaderAlert: boolean;

  /** If true, show help text with certsy */
  useCertsyHelpText: boolean;

  /** If true, use unification-compatible data analytics events */
  unifiedAnalytics: boolean;
}

const SupportedBrands = ['jobstreet', 'seek'] as const;
export const SupportedCountries = [
  'AU',
  'NZ',
  'SG',
  'MY',
  'PH',
  'ID',
  'TH',
  'HK',
] as const;

export type Brand = (typeof SupportedBrands)[number];
export type Country = (typeof SupportedCountries)[number];

export interface Config {
  brand: Brand;
  country: Country;
}

export type FeatureProvider = (
  config: Config,
) => (featureInput: Features) => Features;

export const DefaultFeatures: Features = {
  search: false,
  help: true,
  jobStreetIcon: false,
  newPill: false,
  showHeader: true,
  mustHaveToolTip: false,
  mustHaveNew: false,
  showPublishedHeaderAlert: true,
  useCertsyHelpText: true,
  unifiedAnalytics: false,
};

export function getFeatures(
  config: Config,
  featureOverrides: Partial<Features> = {},
): Features {
  if (!SupportedBrands.includes(config.brand)) {
    throw new Error(
      `Unsupported brand '${config.brand}'; supported values: ${SupportedBrands}`,
    );
  }
  if (!SupportedCountries.includes(config.country)) {
    throw new Error(
      `Unsupported country '${config.country}'; supported values: ${SupportedCountries}`,
    );
  }

  const rulesToApply = [
    applyBranding,
    applyCountry,
    applyFeatureOverrides(featureOverrides),
  ].map((apply) => apply(config));

  return rulesToApply.reduce(
    (features, applyRule) => applyRule(features),
    DefaultFeatures,
  );
}

const applyFeatureOverrides =
  (featureOverrides: Partial<Features>): FeatureProvider =>
  (_) =>
  (featureInput) => ({
    ...featureInput,
    ...featureOverrides,
  });
